import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type dayjs from "dayjs";
import round from "lodash.round";

import { type Kpi } from "@fitness-app/data-models/entities/TrainerStats";
import { type Database } from "@fitness-app/supabase";

import { supabase } from "~/store/initializeStore";

// Extended KPI type with bundle information
export type KpiClientDetail = {
  traineeEmail: string;
  traineeId: string;
  status: "renewed" | "archived";
  isGhost: boolean;
  bundleHistory?: Array<{
    bundleName: string;
    purchaseDate: string;
    amount: number;
  }>;
};

export type ExtendedKpi = Omit<Kpi, "detatils"> & {
  detatils: KpiClientDetail[];
};

type CustomerBundle = Database["public"]["Tables"]["customer_bundle"]["Row"];

export type RenewalKpiEventDetail = {
  order_id: string | null;
  client_email: string;
  renewal_start_at: string | null;
  renewal_month: string | null;
  client_type: "new" | "old" | "unresolved";
  previous_bundle_count: number;
  status: "renewed" | "ended_no_renewal";
  is_ghost: boolean | null;
  traineeId: string | null;
  productClientId: string | null;
  trainerId: string | null;
  trainer_source: string;
  resolution_notes: Record<string, unknown> | null;
};

export type MonthlyKpi = {
  month: string;
  newClients: ExtendedKpi;
  oldClients: ExtendedKpi;
};

interface UseTrainerKpiDataProps {
  selectedMonth: dayjs.Dayjs;
  trainerId: string;
}

const calculatePercentage = (numerator: number, denominator: number) =>
  denominator > 0 ? (numerator / denominator) * 100 : 0;

export const useTrainerKpiData = ({ selectedMonth, trainerId }: UseTrainerKpiDataProps) => {
  const selectedMonthKey = selectedMonth.format("YYYY-MM");
  const selectedYear = selectedMonth.format("YYYY");

  // One fetch per trainer per year: the yearly view renders every month like the workbook,
  // and the single-month view is just a slice of it.
  const {
    data: kpiEvents = [],
    // First load only — background refetches (window focus, remount) must not flash the
    // loader over already-rendered numbers.
    isInitialLoading: isLoadingRenewals,
    isError: renewalEventsUnavailable,
  } = useQuery({
    queryKey: ["trainerRenewalKpiEvents", trainerId, selectedYear],
    queryFn: async () => {
      if (!trainerId) return [];

      const { data, error } = await supabase
        .from("trainer_renewal_kpi_event_details")
        .select(
          "order_id, client_email, renewal_start_at, renewal_month, client_type, previous_bundle_count, status, is_ghost, traineeId, productClientId, trainerId, trainer_source, resolution_notes",
        )
        .gte("renewal_month", `${selectedYear}-01`)
        .lte("renewal_month", `${selectedYear}-12`)
        .eq("trainerId", trainerId)
        .in("status", ["renewed", "ended_no_renewal"]);

      if (error) {
        throw new Error(error.message);
      }

      return (data ?? []) as RenewalKpiEventDetail[];
    },
    enabled: Boolean(trainerId),
  });

  const renewalEvents = useMemo(
    () => kpiEvents.filter((event) => event.status === "renewed" && event.renewal_month === selectedMonthKey),
    [kpiEvents, selectedMonthKey],
  );

  const allEmails = useMemo(() => {
    const emails = new Set<string>();
    kpiEvents.forEach((event) => {
      if (event.client_email) {
        emails.add(event.client_email);
      }
    });
    return Array.from(emails);
  }, [kpiEvents]);

  // Fetch customer bundle data for all emails with trainee information
  const { data: customerBundles, isInitialLoading: isLoadingBundles } = useQuery({
    queryKey: ["customerBundles", allEmails, selectedYear],
    queryFn: async () => {
      if (allEmails.length === 0) return [];

      const { data, error } = await supabase
        .from("customer_bundle")
        .select("*, trainee:traineeId(id, email, ghost)")
        .in("clientEmail", allEmails)
        // fetch only Program Hollywood bundles
        .eq("productId", "prod_lR6lLaCxqDqOWn")
        .order("createdAt", { ascending: true });

      if (error) {
        throw new Error(error.message);
      }

      return data as (CustomerBundle & { trainee: { id: string; email: string; ghost: boolean | null } | null })[];
    },
    enabled: allEmails.length > 0,
  });

  const bundleHistoryByEmail = useMemo(() => {
    return customerBundles?.reduce(
      (acc, bundle) => {
        const email = bundle.clientEmail.toLowerCase().trim();
        if (!acc[email]) {
          acc[email] = [];
        }
        acc[email]!.push(bundle);
        return acc;
      },
      {} as Record<string, typeof customerBundles>,
    );
  }, [customerBundles]);

  // Per-month KPIs for the whole selected year, workbook semantics: ending clients come
  // straight from the fact table (renewals from orders + trainer sheets, plus
  // ended-without-renewal rows), ghosts/active counted among ENDING clients.
  const kpiByMonth = useMemo(() => {
    const getNormalizedBundleHistory = (email: string) => bundleHistoryByEmail?.[email.toLowerCase().trim()] ?? [];
    const getBundleHistory = (email: string) => {
      return getNormalizedBundleHistory(email).map((bundle) => ({
        bundleName: bundle.name,
        purchaseDate:
          (bundle.metadata as Record<string, string>)?.paidDate ||
          (bundle.metadata as Record<string, string>)?.createdAt ||
          bundle.createdAt,
        amount: round(bundle.amount / 100, 2),
      }));
    };

    const buildKpi = (events: RenewalKpiEventDetail[]): ExtendedKpi => {
      const clients = events.map((event) => ({
        email: event.client_email,
        action: event.status === "renewed" ? ("renewed" as const) : ("archived" as const),
        isGhost: event.is_ghost ?? false,
        traineeId: event.traineeId ?? undefined,
      }));
      const renewed = clients.filter((c) => c.action === "renewed");
      const active = clients.filter((c) => !c.isGhost);

      return {
        totalEnding: clients.length,
        totalRenewed: renewed.length,
        ghostRenewals: clients.filter((c) => c.isGhost).length,
        activeRenewals: active.length,
        totalRenewalsPercentage: calculatePercentage(renewed.length, clients.length),
        // Workbook formula ("ile % bez ducha"): renewed / active-ending
        activeRenewalsPercentage: calculatePercentage(renewed.length, active.length),
        detatils: clients.map((client) => ({
          traineeEmail: client.email,
          traineeId: client.traineeId || "",
          status: client.action,
          isGhost: client.isGhost,
          bundleHistory: getBundleHistory(client.email),
        })),
      };
    };

    const months: MonthlyKpi[] = [];
    for (let month = 1; month <= 12; month++) {
      const monthKey = `${selectedYear}-${String(month).padStart(2, "0")}`;
      const monthEvents = kpiEvents.filter((event) => event.renewal_month === monthKey);
      months.push({
        month: monthKey,
        newClients: buildKpi(monthEvents.filter((event) => event.client_type !== "old")),
        oldClients: buildKpi(monthEvents.filter((event) => event.client_type === "old")),
      });
    }
    return months;
  }, [kpiEvents, selectedYear, bundleHistoryByEmail]);

  const kpiData = useMemo(() => {
    const current = kpiByMonth.find((entry) => entry.month === selectedMonthKey) ?? kpiByMonth[0];
    return {
      newClients: current?.newClients,
      oldClients: current?.oldClients,
      renewalEvents,
    };
  }, [kpiByMonth, selectedMonthKey, renewalEvents]);

  return {
    kpiData,
    kpiByMonth,
    renewalEvents,
    // True when the renewal fact view is unreachable (e.g. migrations not applied on this
    // environment yet) — consumers must show a warning instead of silently rendering zeros.
    renewalEventsUnavailable,
    isLoading: isLoadingRenewals || isLoadingBundles,
  };
};
